import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Typography, LinearProgress, Button, Chip, Tooltip, CircularProgress,
} from '@mui/material';
import { DateTime } from 'luxon';
import { supabase } from '../supabaseClient';
import { AuthContext, MyContext } from '../context/AuthContext';
import TimeDuration from '../TimeDuration/index';
import HourglassFullIcon from '../resource/HourglassFullIcon';

interface PollOption {
  id: string;
  poll_id: string;
  label: string;
  position: number;
  time_total_seconds: number;
}

interface Poll {
  id: string;
  post_id: string;
  question: string;
  poll_type: 'standard' | 'survival' | 'race';
  time_per_vote: number;
  race_goal_seconds: number;
  expire_at: string;
  is_expired: boolean;
}

interface PollVote {
  id: string;
  poll_option_id: string;
  user_record_id: string;
  time_contributed: number;
}

const RACE_COLORS = ['#1976d2', '#e65100', '#2e7d32', '#6a1b9a'];

function formatSeconds(s: number): string {
  if (s >= 86400) return `${Math.round(s / 86400)}d`;
  if (s >= 3600) return `${Math.round(s / 3600)}h`;
  if (s >= 60) return `${Math.round(s / 60)}m`;
  return `${s}s`;
}

function PollDisplay({ postId }: { postId: string }) {
  const userRecord = useContext<MyContext>(AuthContext).userRecord;
  const [poll, setPoll] = useState<Poll | null>(null);
  const [options, setOptions] = useState<PollOption[]>([]);
  const [votes, setVotes] = useState<PollVote[]>([]);
  const [myVote, setMyVote] = useState<PollVote | null>(null);
  const [loading, setLoading] = useState(true);
  const [voting, setVoting] = useState(false);
  // Unique per-mount suffix so two PollDisplays for the same post on the
  // same page don't collide on a shared Supabase channel name.
  const channelIdRef = useRef<string>(Math.random().toString(36).slice(2, 10));

  const fetchPoll = useCallback(async () => {
    const { data: pollData } = await supabase
      .from('polls').select('*').eq('post_id', postId).maybeSingle();
    if (!pollData) { setLoading(false); return; }
    setPoll(pollData as Poll);

    const [{ data: opts }, { data: vs }] = await Promise.all([
      supabase.from('poll_options').select('*').eq('poll_id', pollData.id).order('position'),
      supabase.from('poll_votes').select('*').eq('poll_id', pollData.id),
    ]);
    setOptions((opts ?? []) as PollOption[]);
    setVotes((vs ?? []) as PollVote[]);
    if (userRecord && vs) {
      const mine = (vs as PollVote[]).find((v) => v.user_record_id === userRecord.id);
      setMyVote(mine ?? null);
    }
    setLoading(false);
  }, [postId, userRecord]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  // Subscribe to realtime updates scoped to the actual poll.id (not the post.id)
  useEffect(() => {
    if (!poll?.id) return;
    const channel = supabase
      .channel(`poll-${poll.id}-${channelIdRef.current}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'poll_votes', filter: `poll_id=eq.${poll.id}` }, fetchPoll)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'poll_options', filter: `poll_id=eq.${poll.id}` }, fetchPoll)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [poll?.id, fetchPoll]);

  const handleVote = async (option: PollOption) => {
    if (!userRecord || !poll || myVote || voting) return;
    setVoting(true);
    try {
      const timeContrib = poll.poll_type !== 'standard' ? poll.time_per_vote : 0;

      // Insert vote
      const { error } = await supabase.from('poll_votes').insert({
        poll_id: poll.id,
        poll_option_id: option.id,
        user_record_id: userRecord.id,
        time_contributed: timeContrib,
      });
      if (error) throw error;

      // For race/survival: accumulate time on option + deduct from voter wallet
      if (poll.poll_type !== 'standard' && timeContrib > 0) {
        await Promise.all([
          supabase.from('poll_options')
            .update({ time_total_seconds: option.time_total_seconds + timeContrib })
            .eq('id', option.id),
          supabase.from('users')
            .update({ time_wallet_seconds: userRecord.time_wallet_seconds - timeContrib })
            .eq('id', userRecord.id),
        ]);

        // Survival: winning/losing option affects post expiry (atomic server-side)
        if (poll.poll_type === 'survival') {
          const isKeep = option.position === 0;
          const delta = isKeep ? timeContrib : -timeContrib;
          const { error: rpcError } = await supabase.rpc('adjust_post_expiry', {
            p_post_id: poll.post_id,
            p_delta_seconds: delta,
          });
          if (rpcError) console.error('adjust_post_expiry failed:', rpcError);
        }

        // Race: first to cross the goal wins — extend post and mark poll done
        if (poll.poll_type === 'race') {
          const newTotal = option.time_total_seconds + timeContrib;
          if (newTotal >= poll.race_goal_seconds && !poll.is_expired) {
            const { data: postRow } = await supabase
              .from('posts').select('expire_at').eq('id', poll.post_id).single();
            if (postRow) {
              const ext = DateTime.fromISO(postRow.expire_at).plus({ seconds: poll.race_goal_seconds });
              await Promise.all([
                supabase.from('polls').update({ is_expired: true }).eq('id', poll.id),
                supabase.from('posts').update({ expire_at: ext.toISO() }).eq('id', poll.post_id),
              ]);
            }
          }
        }
      }

      await fetchPoll();
    } catch (e) {
      console.error('Poll vote failed:', e);
    } finally {
      setVoting(false);
    }
  };

  if (loading) return null;
  if (!poll) return null;

  const totalVotes = votes.length;
  const isExpired = poll.is_expired || DateTime.fromISO(poll.expire_at) < DateTime.now();
  const showResults = !!myVote || isExpired;
  const walletOk = userRecord && (poll.poll_type === 'standard' || userRecord.time_wallet_seconds >= poll.time_per_vote);

  return (
    <Box
      sx={{
        mt: 1.5, mb: 0.5, p: 1.5, borderRadius: 2,
        border: '1px solid', borderColor: 'divider',
        bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
      }}
    >
      {/* Poll header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        {poll.poll_type === 'standard' && (
          <Chip label="POLL" size="small" sx={{ fontFamily: '"VT323", monospace', letterSpacing: 1, fontSize: '0.75rem', height: 20 }} />
        )}
        {poll.poll_type === 'survival' && (
          <Chip label="⏳ SURVIVAL VOTE" size="small" color="warning" variant="outlined" sx={{ fontFamily: '"VT323", monospace', fontSize: '0.75rem', height: 20 }} />
        )}
        {poll.poll_type === 'race' && (
          <Chip label="🏁 TIME RACE" size="small" color="error" variant="outlined" sx={{ fontFamily: '"VT323", monospace', fontSize: '0.75rem', height: 20 }} />
        )}
        {!isExpired && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            <TimeDuration jsDate={DateTime.fromISO(poll.expire_at).toJSDate()} /> left
          </Typography>
        )}
        {isExpired && <Chip label="CLOSED" size="small" color="default" sx={{ ml: 'auto', fontSize: '0.7rem', height: 18 }} />}
      </Box>

      {poll.question && (
        <Typography variant="body2" fontWeight={700} sx={{ mb: 1.25, lineHeight: 1.4 }}>
          {poll.question}
        </Typography>
      )}

      {/* Race goal banner */}
      {poll.poll_type === 'race' && poll.race_goal_seconds > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontStyle: 'italic' }}>
          First to {formatSeconds(poll.race_goal_seconds)} extends this post by {formatSeconds(poll.race_goal_seconds)}
        </Typography>
      )}

      {/* Survival cost banner */}
      {poll.poll_type === 'survival' && poll.time_per_vote > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, fontStyle: 'italic' }}>
          <HourglassFullIcon style={{ width: 12, height: 12 }} />
          Each vote costs {formatSeconds(poll.time_per_vote)} from your wallet
        </Typography>
      )}

      {/* Options */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {options.map((opt, i) => {
          const optVotes = votes.filter((v) => v.poll_option_id === opt.id).length;
          const pct = totalVotes > 0 ? Math.round((optVotes / totalVotes) * 100) : 0;
          const isMyChoice = myVote?.poll_option_id === opt.id;
          const color = poll.poll_type === 'survival'
            ? (opt.position === 0 ? '#2e7d32' : '#b51e1b')
            : RACE_COLORS[i % RACE_COLORS.length];
          const racePct = poll.poll_type === 'race' && poll.race_goal_seconds > 0
            ? Math.min(100, Math.round((opt.time_total_seconds / poll.race_goal_seconds) * 100))
            : 0;

          return (
            <Box key={opt.id}>
              {showResults ? (
                // Results view
                <Box
                  sx={{
                    position: 'relative', borderRadius: 1.5, overflow: 'hidden',
                    border: '1px solid', borderColor: isMyChoice ? color : 'divider',
                    bgcolor: 'background.paper',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute', inset: 0, left: 0, top: 0,
                      width: `${poll.poll_type === 'race' ? racePct : pct}%`,
                      bgcolor: color, opacity: 0.12, transition: 'width 0.6s ease',
                    }}
                  />
                  <Box sx={{ position: 'relative', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      {isMyChoice && <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />}
                      <Typography variant="body2" fontWeight={isMyChoice ? 700 : 400}>
                        {opt.label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {poll.poll_type === 'race' && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {formatSeconds(opt.time_total_seconds)} / {formatSeconds(poll.race_goal_seconds)}
                        </Typography>
                      )}
                      {poll.poll_type !== 'race' && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                          {pct}% ({optVotes})
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {poll.poll_type === 'race' && (
                    <LinearProgress
                      variant="determinate"
                      value={racePct}
                      sx={{ height: 3, bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: color } }}
                    />
                  )}
                </Box>
              ) : (
                // Voting view
                <Tooltip
                  title={!userRecord ? 'Sign in to vote' : !walletOk ? `Not enough time (need ${formatSeconds(poll.time_per_vote)})` : ''}
                  disableHoverListener={!!userRecord && !!walletOk}
                >
                  <span>
                    <Button
                      fullWidth
                      variant="outlined"
                      size="small"
                      disabled={!userRecord || !walletOk || voting}
                      onClick={() => handleVote(opt)}
                      sx={{
                        justifyContent: 'flex-start', textTransform: 'none',
                        fontWeight: 500, fontSize: '0.875rem',
                        borderColor: color, color: color,
                        '&:hover': { bgcolor: `${color}14`, borderColor: color },
                      }}
                    >
                      {voting ? <CircularProgress size={14} sx={{ mr: 1 }} /> : null}
                      {opt.label}
                      {poll.poll_type !== 'standard' && poll.time_per_vote > 0 && (
                        <Box component="span" sx={{ ml: 'auto', fontSize: '0.72rem', opacity: 0.7 }}>
                          -{formatSeconds(poll.time_per_vote)}
                        </Box>
                      )}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Footer */}
      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
        {totalVotes} vote{totalVotes !== 1 ? 's' : ''}
        {poll.poll_type === 'standard' && !showResults && !isExpired && ' · vote to see results'}
      </Typography>
    </Box>
  );
}

export default PollDisplay;
